/**
 * Template Service
 *
 * API service for template marketplace operations.
 */
import api from './api';
import {
  Template,
  CreateTemplateRequest,
  UpdateTemplateRequest,
  TemplateListResponse,
  TemplateFilters,
} from '../types/template';
import { Agent } from '../types/agent';
import { AgentType } from '../types/agent';
import { TemplateCategory } from '../types/template';

/** Payload for publishing an agent as a marketplace template. */
export interface PublishTemplateData {
  name: string;
  description: string;
  category: TemplateCategory;
  type: AgentType;
  configuration: Record<string, unknown>;
  tags?: string[];
}

/** Result of a publish-for-review submission. */
export interface PublishTemplateResult {
  id?: string;
  status?: string;
  message?: string;
}

/** Normalize API response to match frontend Template type */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function normalizeTemplate(t: any): Template {
  // Map flat authorId/authorName/authorEmail to nested author object
  const author = t.author ?? (t.authorId ? {
    id: t.authorId,
    name: t.authorName ?? null,
    avatar: undefined,
  } : undefined);

  return {
    ...t,
    tags: t.tags ?? t.defaultTags ?? [],
    rating: t.rating ?? 0,
    ratingCount: t.ratingCount ?? t.rating_count ?? 0,
    usageCount: t.usageCount ?? 0,
    configuration: t.configuration ?? {},
    author,
  };
}

export const templateService = {
  /**
   * List templates with pagination and filters
   *
   * @param filters - Query parameters for filtering and pagination
   * @returns Paginated list of templates
   */
  async list(filters?: TemplateFilters): Promise<TemplateListResponse> {
    const response = await api.get<TemplateListResponse>('/templates', {
      params: filters,
    });
    return {
      ...response.data,
      data: response.data.data.map(normalizeTemplate),
    };
  },

  /**
   * Get a single template by ID
   *
   * @param templateId - The template ID to fetch
   * @returns Template details
   */
  async get(templateId: string): Promise<Template> {
    const response = await api.get<Template>(`/templates/${templateId}`);
    return normalizeTemplate(response.data);
  },

  /**
   * Create a new template
   *
   * @param data - Template creation data
   * @returns Created template
   */
  async create(data: CreateTemplateRequest): Promise<Template> {
    const response = await api.post<Template>('/templates', data);
    return response.data;
  },

  /**
   * Publish an agent's configuration as a marketplace template. The template is
   * created with PENDING status and becomes public once an admin approves it.
   *
   * The backend endpoint takes the text fields as query params and the agent
   * configuration as the JSON body.
   */
  async publishAsTemplate(data: PublishTemplateData): Promise<PublishTemplateResult> {
    const qs = new URLSearchParams();
    qs.set('name', data.name);
    qs.set('description', data.description);
    qs.set('category', data.category);
    qs.set('type', data.type);
    qs.set('visibility', 'public');
    (data.tags ?? []).forEach((t) => qs.append('tags', t));
    const response = await api.post<PublishTemplateResult>(
      `/marketplace/templates/publish?${qs.toString()}`,
      data.configuration ?? {}
    );
    return response.data;
  },

  /**
   * Update a template
   *
   * @param templateId - The template ID to update
   * @param data - Update data
   * @returns Updated template
   */
  async update(templateId: string, data: UpdateTemplateRequest): Promise<Template> {
    const response = await api.patch<Template>(`/templates/${templateId}`, data);
    return response.data;
  },

  /**
   * Delete a template
   *
   * @param templateId - The template ID to delete
   */
  async delete(templateId: string): Promise<void> {
    await api.delete(`/templates/${templateId}`);
  },

  /**
   * Use a template to create an agent
   *
   * @param templateId - The template ID to use
   * @param name - Optional custom name for the created agent
   * @returns Response with created agent info
   */
  async useTemplate(templateId: string, name?: string): Promise<Agent> {
    const response = await api.post<Agent>(
      `/templates/${templateId}/use`,
      name ? { name } : {}
    );
    return response.data;
  },

  /**
   * Rate a template
   *
   * @param templateId - The template ID to rate
   * @param rating - Rating value (1-5)
   * @returns Updated template
   */
  async rate(templateId: string, rating: number): Promise<Template> {
    const response = await api.post<Template>(`/templates/${templateId}/rate`, {
      rating,
    });
    return response.data;
  },

  /**
   * Fork a template
   */
  async fork(templateId: string, name?: string): Promise<Template> {
    const response = await api.post<Template>(`/templates/${templateId}/fork`, {
      name,
    });
    return normalizeTemplate(response.data);
  },

  /**
   * Get featured templates
   *
   * @returns List of featured templates
   */
  async getFeatured(): Promise<Template[]> {
    const response = await api.get<TemplateListResponse>('/templates/featured');
    return response.data.data;
  },

  /**
   * Get popular templates
   *
   * @param limit - Number of templates to return
   * @returns List of popular templates
   */
  async getPopular(limit: number = 10): Promise<Template[]> {
    const response = await api.get<TemplateListResponse>('/templates/popular', {
      params: { limit },
    });
    return response.data.data;
  },

  /**
   * Get category counts
   *
   * @returns Map of category to count
   */
  async getCategoryCounts(): Promise<Record<string, number>> {
    const response = await api.get<Record<string, number>>('/templates/categories/counts');
    return response.data;
  },
};

export default templateService;
